import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { FC, useState } from "react";
import { useChangePasswordMutation } from "../../../../features/api/Api";
import Swal from "sweetalert2";
import i18n from "../../../../locales/i18n";
import { email_regex, password_regex } from "../../../../utils/regex";
import { useTranslation } from "react-i18next";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { red } from "@mui/material/colors";

interface IProps {
  open: boolean;
  onClose: () => void;
  email: string;
  customerCode: string;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

const ChangePasswordModal: FC<IProps> = ({
  open,
  onClose,
  email,
  customerCode,
}) => {
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const handleClickShowOldPassword = () => setShowOldPassword((show) => !show);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);
  const [t] = useTranslation();

  const validationSchema = yup.object({
    email: yup
      .string()
      .email(t("resources.users.login.valid.valid_email"))
      .required(t("resources.users.login.valid.required_email"))
      .test(
        t("resources.users.login.valid.not_valid_email"),
        t("resources.users.login.valid.not_valid_email"),
        function () {
          return email_regex.test(this.parent.email) ? false : true;
        }
      ),

    oldPassword: yup
      .string()
      .required(i18n.t("resources.changePassword.valid.enter_oldPassword"))
      .min(
        8,
        ({ min }) =>
          `${i18n.t("resources.changePassword.password")} ${min} ${i18n.t(
            "resources.changePassword.valid.invalid_lenght"
          )}`
      ),

    confirmPassword: yup
      .string()
      .required(i18n.t("resources.changePassword.valid.enter_password"))
      .test(function (value, { createError, path }) {
        if (this.parent.password !== this.parent.confirmPassword)
          return createError({
            message: i18n.t("resources.changePassword.valid.equal_password"),
            path: path,
          });

        return true;
      }),
    password: yup
      .string()
      .required(i18n.t("resources.changePassword.valid.enter_password"))
      .min(
        8,
        ({ min }) =>
          `${i18n.t("resources.changePassword.password")} ${min} ${i18n.t(
            "resources.changePassword.valid.invalid_lenght"
          )}`
      )
      .test(
        i18n.t("resources.changePassword.valid.password"),
        i18n.t("resources.changePassword.valid.password"),
        function () {
          return password_regex.test(this.parent.password);
        }
      ),
  });

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        ...values,
        customerCode, // buraya ekstra veriyi ekliyoruz
      };

      await changePassword(payload).unwrap();

      Swal.fire({
        icon: "success",
        title: t("actions.successfully"),
        text: t("actions.password_reset_success"),
      });
      onClose();
    } catch (error: any) {
      const message = error.data?.data || "Bir hata oluştu.";
      Swal.fire({
        icon: "error",
        title: "Hata",
        text: message,
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="change-password-modal"
    >
      <Box sx={style}>
        <Typography
          id="change-password-modal"
          variant="h5"
          sx={{ mb: 2, color: "red", textAlign: "center" }}
        >
          Şifrenizin süresi dolmuştur. <br />
          Lütfen aşağıdaki ekrandan yeni bir şifre oluşturunuz.
        </Typography>
        <Typography id="change-password-modal" variant="h6" sx={{ mb: 2 }}>
          Şifre Değiştir
        </Typography>
        <Formik
          initialValues={{
            email,
            oldPassword: "",
            password: "",
            confirmPassword: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, errors, touched }) => (
            <Form>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={values.email}
                onChange={handleChange}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                style={{ marginBottom: "20px" }}
              />

              <TextField
                fullWidth
                type={showOldPassword ? "text" : "password"}
                id="oldPassword"
                name="oldPassword"
                label={t("resources.users.login.old_password")}
                value={values.oldPassword}
                onChange={handleChange}
                error={touched.oldPassword && Boolean(errors.oldPassword)}
                helperText={touched.oldPassword && errors.oldPassword}
                style={{ marginBottom: "20px" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "5px" }}>
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowOldPassword}
                        edge="end"
                      >
                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                label={t("resources.users.login.new_password")}
                value={values.password}
                onChange={handleChange}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                style={{ marginBottom: "20px" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "5px" }}>
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                label={t("resources.users.login.confirm_password")}
                value={values.confirmPassword}
                onChange={handleChange}
                error={
                  touched.confirmPassword && Boolean(errors.confirmPassword)
                }
                helperText={touched.confirmPassword && errors.confirmPassword}
                style={{ marginBottom: "20px" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "5px" }}>
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mt: 3 }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Şifreyi Güncelle"
                )}
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default ChangePasswordModal;
